// ArrivalOverlay — fullscreen overlay when a new audio is released.
// Tap anywhere → onEnter(). Tries Vibration API (Android Chrome only; iOS
// Safari ignores silently). Render via portal-less absolute positioning so
// it covers whichever route is active.

import { useEffect } from "react";
import { BreathDot } from "./atoms/BreathDot";
import { Han } from "./atoms/Han";
import { T } from "./tokens";

export interface ArrivalOverlayProps {
  /** Title to show — the newly arrived audio's title */
  title: string;
  /** Subtitle / intro text */
  intro?: string;
  /** Tap handler — usually navigates to the player */
  onEnter: () => void;
}

export function ArrivalOverlay({ title, intro, onEnter }: ArrivalOverlayProps) {
  useEffect(() => {
    // Feature-detect: Vibration API only fires on Android Chrome via user gesture.
    // We try it on mount; failure is silent on iOS Safari.
    try {
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        (navigator as Navigator & { vibrate: (pattern: number | number[]) => boolean }).vibrate(
          [40, 60, 40],
        );
      }
    } catch {
      /* no-op */
    }
  }, []);

  return (
    <button
      onClick={onEnter}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(250,250,250,0.96)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 28,
        padding: 40,
        textAlign: "center",
        width: "100%",
        border: "none",
        cursor: "pointer",
        animation: "gb-arrive 700ms ease-out both",
      }}
    >
      <BreathDot ripple />
      <Han size={13} color={T.ink500}>
        一段聲音剛到了
      </Han>
      <Han size={24} color={T.ink900} lh={1.5}>
        {title}
      </Han>
      {intro && (
        <Han size={13} color={T.ink500} style={{ maxWidth: 240 }}>
          {intro}
        </Han>
      )}
      <Han size={12} color={T.ink350} style={{ marginTop: 12 }}>
        輕觸畫面,現在聽
      </Han>
      <style>{`
        @keyframes gb-arrive {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
        /* 2026-05-30: reduce-motion opt-out removed — every participant
           gets the full animation regardless of OS setting. */
      `}</style>
    </button>
  );
}
